import * as React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import {
  fetchAddReturnDataRequest,
  fetchDefaultTaxRatesRequest,
  mapTaxRatesToBoxes,
  resetAiMappingStatus,
  saveMappingRequest,
} from 'store/returns/actions';
import {
  getAiMappingStatus,
  getReturnData,
  getReturnLoading,
  getReturnPreLoading,
  getReturnTaxRatesLoading,
  getDefaultTaxRatesData,
  getTaxRatesToBoxesMapping,
} from 'store/returns/selectors';
import { MappingForm } from 'components/MappingForm';
import { useState } from 'react';
import { MappingData } from 'store/returns/models';
import {
  getOrgMappedOrganisations,
  getOrgMappings,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { getTaxRatesForConnection } from 'store/tax-rates/selectors';
import moment from 'moment';
import { formatDisplayDate } from 'i18n/calendarLabels';
import { calculateNextPeriod } from 'utils/calculate-next-period';
import { Form, Spin } from 'antd';
import { Section, TaxRate } from 'store/returns/models/returnData';
import { MappingTaxRate } from 'store/returns/models/mappingTaxRate';
import {
  turnDrawerOn,
  // turnDrawerOff,
  turnMessageOn,
  turnModalOff,
  turnModalOn,
  clearMappingData,
  updateMappingData,
} from 'store/app/actions';
import { MessageStates } from 'containers/MessageBox';
import { DrawerData, MessageData, ModalData } from 'store/app/types';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { getMappingData } from 'store/app/selectors';
import {
  DrawerType,
  LicenseType,
  TaxRateType,
  WindowType,
} from 'store/app/enums';
import { ChooseReturn } from 'components/MappingForm/choose-return';
import routes from 'routes';
import { ChooseTemplate } from 'components/MappingForm/choose-template';
import { getCommonCountries } from 'store/common/selectors';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import GenTaxImage from 'assets/images/gen-tax-img.svg';
import * as _ from 'lodash';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { checkPageLoaded } from 'utils/check-page-loaded';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { AdvancedUserRightsCodes } from 'store/app/enums';
import { AccountType, Provider } from 'store/api/enums';
import {
  PreferenceSections,
  PreferenceActions,
  Preference,
  usePreferences,
} from 'utils/preferences';
import { getBillingHasAdvancedRights } from 'store/billing/selectors';
import { getProviderName } from 'utils/get-provider-name';
import { parseGroupReturnContextFromSearch } from 'utils/group-return-context';
import { useHistoryBlock } from 'hooks/useHistoryBlock';
import {
  deriveOssCountryIdsFromJsonMapping,
  getMappedSubsectionCodesFromSelectedRates,
  getUnmappedOssCountrySections,
  isLeavingMappingPage,
  isOssCodeAmongRemoved,
  isOssReturnType,
  pruneCodesFromJsonMapping,
} from 'utils/oss-mapping';

export interface StepOneData {
  entity?: string;
  country?: string;
  typeName?: string;
  firstPeriod?: string;
  firstDueDate?: string;
  providerName?: string;
  shortCode?: string;
  organisationId?: string;
}

type MappingNavigationWarning = 'unsaved' | 'oss-unmapped';

export type TaxRateOption = {
  label: string;
  value: string;
};

export interface SelectedRate {
  value: string;
  location: string[];
  cashAccounting: boolean;
  reverseCharge: number | null;
  taxRateType: TaxRateType;
}

export interface TemplateMapping {
  mappingId: string;
  organisationName: string;
  createdDate: string;
  country?: string;
}

const AI_TEMPLATE_MAPPING_ID = '__AI_MAPPING__';

export interface ReverseCharge {
  rateId: string;
  used: boolean;
  amount: number | null;
}

/** Collapse tabs, non-breaking spaces, and repeated spaces so copied tax names still match. */
const normalizeTaxRateName = (value?: string | null) =>
  (value ?? '')
    .replace(/\u00a0/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const sameTaxPercentage = (
  sourceRate: string | undefined,
  targetRate: number | undefined
) => {
  if (sourceRate == null || sourceRate === '' || targetRate == null) return false;
  const source = Number(sourceRate);
  if (Number.isNaN(source)) return false;
  return source.toFixed(2) === Number(targetRate).toFixed(2);
};

/**
 * Copy another entity's box mapping onto this entity's tax rates.
 * Names often differ only by extra spaces. Rates that still don't exist here are omitted.
 */
const remapTemplateMappingRates = (
  sourceRows: MappingTaxRate[],
  targetRates: TaxRate[] | undefined
): MappingTaxRate[] => {
  const unused = [...(targetRates ?? [])];
  const remapped: MappingTaxRate[] = [];

  sourceRows.forEach((row) => {
    const sourceKey = normalizeTaxRateName(row.taxCodeName);
    if (!sourceKey) return;

    const nameMatches = unused.filter(
      (rate) => normalizeTaxRateName(rate.name) === sourceKey
    );
    if (nameMatches.length === 0) return;

    const percentageMatches = nameMatches.filter((rate) =>
      sameTaxPercentage(row.taxRate, rate.taxRate)
    );
    const chosen =
      percentageMatches[0] ?? (nameMatches.length === 1 ? nameMatches[0] : undefined);
    if (!chosen) return;

    const chosenIndex = unused.findIndex((rate) => rate.taxType === chosen.taxType);
    if (chosenIndex > -1) unused.splice(chosenIndex, 1);

    remapped.push({
      ...row,
      taxType: `${chosen.taxType}`,
    });
  });

  return remapped;
};

export const AddReturnPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const parameters: any = useParams();
  const currentUser = useSelector(getAuthUser);
  const conId = parameters.connectionId;
  const mappingId = parameters.mappingId;
  const mappings = useSelector(getOrgMappings);
  const organisations = useSelector(getOrgOrganisations);
  const mappedOrganisations = useSelector(getOrgMappedOrganisations);
  const preLoading = useSelector(getReturnPreLoading);
  const taxRatesLoading = useSelector(getReturnTaxRatesLoading);
  const loading = useSelector(getReturnLoading);
  const returnData = useSelector(getReturnData);
  const defaultTaxRatesData = useSelector(getDefaultTaxRatesData);
  const mappingData = useSelector(getMappingData);
  const countries = useSelector(getCommonCountries);
  const hasAdvancedUserRightsPre = useSelector(getBillingHasAdvancedRights);
  const userLicence = useSelector(getAuthUserLicence);
  const taxRatesToBoxesMapping = useSelector(getTaxRatesToBoxesMapping);
  const aiMappingStatus = useSelector(getAiMappingStatus);
  // const selectedEntityAccounts = useSelector(getSelectedEntityAccounts);
  const [form2] = Form.useForm();
  const [stepOneData, setStepOneData] = useState<StepOneData>({});
  const editMode: boolean = !!mappingId;
  const [selectedReturnType, setSelectedReturnType] = useState<string>();
  const [taxRateOptions, setTaxRateOptions] = useState<TaxRateOption[]>();
  const [outOfScopeOptions, setOutOfScopeOptions] = useState<TaxRateOption[]>();
  const [sections, setSections] = useState<Section[]>();
  const [cashAccounting, setCashAccounting] = useState<boolean>(false);
  const [outOfScopeCode, setOutOfScopeCode] = useState<string>('OOS');
  const [selectedRates, setSelectedRates] = useState<SelectedRate[]>([]);
  const [selectedRatesCopy, setSelectedRatesCopy] = useState<SelectedRate[]>(
    []
  );
  const [oldSelectedRates, setOldSelectedRates] = useState<SelectedRate[]>([]);

  const navigate = useNavigate();
  const [dataSaved, setDataSaved] = React.useState<boolean>(false);
  const [sectionTouched, setSectionTouched] = React.useState<boolean>(false);
  const [templateMappings, setTemplateMappings] = useState<TemplateMapping[]>(
    []
  );
  const [reverseCharges, setReverseCharges] = useState<ReverseCharge[]>([]);
  const [hasAdvancedUserRights, setHasAdvancedUserRights] =
    useState<boolean>(false);
  const prevOssCountryIdsRef = React.useRef<string[] | undefined>(undefined);
  const skipUnsavedNavigationBlockRef = React.useRef(false);
  const skipMappingNavigationWarningsRef = React.useRef(false);
  const blockedProceedRef = React.useRef<(() => void) | undefined>();
  const applyAiMappingRef = React.useRef(false);
  const requestedSectionsKeyRef = React.useRef<string>();
  const unsavedNavigationModalShownRef = React.useRef(false);
  const isLeavingWithoutSaveRef = React.useRef(false);
  const sectionTouchedRef = React.useRef(sectionTouched);
  const dataSavedRef = React.useRef(dataSaved);
  const mappingDataRef = React.useRef(mappingData);
  const selectedRatesRef = React.useRef(selectedRates);
  const pendingNavigationWarningsRef = React.useRef<MappingNavigationWarning[]>(
    []
  );

  sectionTouchedRef.current = sectionTouched;
  dataSavedRef.current = dataSaved;
  mappingDataRef.current = mappingData;
  selectedRatesRef.current = selectedRates;

  const markSectionTouched = React.useCallback((value: boolean) => {
    if (value) {
      setDataSaved(false);
    }
    setSectionTouched(value);
  }, []);

  const getPendingNavigationWarnings = React.useCallback(
    (nextPathname: string): MappingNavigationWarning[] => {
      if (!isLeavingMappingPage(location.pathname, nextPathname)) {
        return [];
      }

      const warnings: MappingNavigationWarning[] = [];

      if (!dataSavedRef.current && sectionTouchedRef.current) {
        warnings.push('unsaved');
      }

      const unmappedSections = getUnmappedOssCountrySections(
        mappingDataRef.current?.ossCountryIds || [],
        returnData?.sections,
        mappingDataRef.current?.typeId,
        getMappedSubsectionCodesFromSelectedRates(selectedRatesRef.current)
      );

      if (unmappedSections.length > 0) {
        warnings.push('oss-unmapped');
      }

      return warnings;
    },
    [location.pathname, returnData?.sections]
  );

  const blocker = useHistoryBlock((nextLocation) => {
    if (skipUnsavedNavigationBlockRef.current) {
      skipUnsavedNavigationBlockRef.current = false;
      return true;
    }

    if (skipMappingNavigationWarningsRef.current) {
      skipMappingNavigationWarningsRef.current = false;
      if (!isLeavingMappingPage(location.pathname, nextLocation.pathname)) {
        return true;
      }
    }

    return getPendingNavigationWarnings(nextLocation.pathname).length === 0;
  });

  React.useEffect(() => {
    if (blocker.state === 'blocked' && blocker.proceed) {
      blockedProceedRef.current = blocker.proceed;
      return;
    }

    blockedProceedRef.current = undefined;
    if (blocker.state === 'unblocked') {
      unsavedNavigationModalShownRef.current = false;
      isLeavingWithoutSaveRef.current = false;
    }
  }, [blocker.state, blocker.proceed]);

  const proceedBlockedNavigation = React.useCallback(() => {
    if (blocker.state !== 'blocked') {
      return;
    }

    const proceed = blockedProceedRef.current ?? blocker.proceed;
    blockedProceedRef.current = undefined;

    if (proceed) {
      proceed();
      return;
    }

    const nextLocation = blocker.location;
    if (nextLocation) {
      blocker.reset?.();
      navigate({
        pathname: nextLocation.pathname,
        search: nextLocation.search || '',
      });
    }
  }, [blocker, navigate]);

  React.useEffect(() => {
    setHasAdvancedUserRights(
      hasAdvancedUserRightsPre || userLicence?.licenseType === LicenseType.TRIAL
    );
  }, [hasAdvancedUserRightsPre, userLicence]);

  // Redirect STAFF users without create/edit rights back to main with a warning
  React.useEffect(() => {
    if (!currentUser || !organisations || !conId) return;
    const selectedOrg = organisations.find((o) => o.connectionId === conId);
    const isStaff = AccountType[currentUser.accountType] === AccountType.STAFF;
    const hasRight =
      !hasAdvancedUserRights ||
      hasAdvancedUserRight(
        currentUser,
        selectedOrg as any,
        AdvancedUserRightsCodes.CREATE_EDIT_RETURN
      );
    if (isStaff && !hasRight) {
      const message: MessageData = {
        title: t('common.attention'),
        description: t('global.edit-return-rights-warning'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      navigate(routes.main, { replace: true });
    }
  }, [currentUser, organisations, conId]);

  const preferences = usePreferences();
  const handlePreferences = (
    preferenceSection: PreferenceSections,
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser)
      return preferences(
        action,
        preferenceSection,
        preference,
        currentUser?.userUuid
      );
    return false;
  };

  const editInitialData = () => {
    if (conId) {
      const drawerData: DrawerData = {
        title: t('submissions.add-tax-return-title'),
        type: DrawerType.ADD_TAX_RETURN,
        component: (
          <ChooseReturn editMode={true} setSectionTouched={markSectionTouched} />
        ),
      };
      dispatch(turnDrawerOn(drawerData));
    }
  };

  const setMappingData = (data: MappingData) => {
    dispatch(updateMappingData(data));
  };

  const updateSelectedRates = (mappedData: SelectedRate[]) => {
    setSelectedRates(mappedData);
    setSelectedRatesCopy([...mappedData]);
  };

  const saveMappingData = (mData: MappingData, stayOnPage = true) => {
    const CASH = 'Cash Accounting';
    const IGNORE = 'Ignore';
    mData?.jsonMapping?.map((item) => {
      if (item?.codes?.includes(CASH) && item?.codes?.includes(IGNORE)) {
        item.codes = item.codes.replace(CASH + ',', '');
      }
    });

    if (conId) {
      const orgId = organisations?.find((o) => o.connectionId === conId)?.uuid;
      const { fromGroup, groupId } = parseGroupReturnContextFromSearch(
        location.search
      );
      const redirectOrganisationId =
        fromGroup && groupId ? groupId : orgId || '';
      dispatch(
        saveMappingRequest(
          conId,
          mData,
          orgId || '',
          redirectOrganisationId,
          stayOnPage
        )
      );
    }
  };

  const openTemplateWindow = () => {
    const drawerData: DrawerData = {
      title: t('submissions.load-from-another-entity-title'),
      type: DrawerType.LOAD_FROM_OTHER_ENTITY,
      component: (
        <ChooseTemplate
          templateMappings={templateMappings}
          loadExistingMapping={loadExistingMapping}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const openDefaultMappingsWindow = () => {
    loadDefaultMappings();
  };

  const loadDefaultMappings = () => {
    if (
      !defaultTaxRatesData?.defaultTaxRates ||
      !returnData?.sections ||
      !returnData?.taxRates
    ) {
      return;
    }

    // Reset dropdown options to show all available tax rates
    const opts: TaxRateOption[] = prepareTaxRateOptions();
    setTaxRateOptions(opts);
    setOutOfScopeOptions(opts);

    // Start with all tax rates (like resetSelectedRates does)
    const newSelectedRates: SelectedRate[] = [];
    returnData.taxRates.forEach((tr) => {
      newSelectedRates.push({
        value: tr.taxType,
        location: [],
        cashAccounting: false,
        reverseCharge: null,
        taxRateType: TaxRateType.INPUT,
      });
    });

    const rcState = getEmptyReverseChargeState();

    const newFormValues: any = {};

    // Now update only the ones that have default mappings
    defaultTaxRatesData.defaultTaxRates.forEach((defaultTaxRate) => {
      const locations: string[] = [];

      // Find subsections using subsectionIds
      defaultTaxRate.subsectionIds.forEach((subsectionId) => {
        returnData.sections?.forEach((section) => {
          section.subsections.forEach((subsection) => {
            if (subsection.id === subsectionId) {
              locations.push(subsection.code);
              // Add to form values
              if (!newFormValues[subsection.code]) {
                newFormValues[subsection.code] = [];
              }
              newFormValues[subsection.code].push(defaultTaxRate.taxRateId);
            }
          });
        });
      });

      // Update the corresponding selected rate
      const selectedRateIndex = newSelectedRates.findIndex(
        (sr) => sr.value === defaultTaxRate.taxRateId
      );
      if (selectedRateIndex !== -1) {
        newSelectedRates[selectedRateIndex].location = locations;
        newSelectedRates[selectedRateIndex].taxRateType =
          defaultTaxRate.taxRateType;
      }

      const rcPercentage = defaultTaxRate.reverseChargePercentage;

      if (rcPercentage != null) {
        const targetIndex = rcState.findIndex(
          (rc) => rc.rateId === defaultTaxRate.taxRateId
        );

        if (targetIndex !== -1) {
          newSelectedRates[selectedRateIndex].reverseCharge = rcPercentage;

          const updatedRcState = rcState.map((rc, index) =>
            index === targetIndex
              ? { ...rc, used: true, amount: rcPercentage }
              : rc
          );

          setReverseCharges(updatedRcState);
        }
      }
    });

    // Clear the form first
    form2.resetFields();

    // Update the selected rates
    setSelectedRates(newSelectedRates);
    setSelectedRatesCopy([...newSelectedRates]);

    // Update form fields with new values
    form2.setFieldsValue(newFormValues);
  };

  const setOptions = (
    trOptions: TaxRateOption[],
    oosOptions: TaxRateOption[]
  ) => {
    setTaxRateOptions(trOptions);
    setOutOfScopeOptions(oosOptions);
  };

  const prepareTaxRateOptions = () => {
    const opts: TaxRateOption[] = [];
    returnData?.taxRates?.map((tr) =>
      opts.push({
        value: tr.taxType,
        label:
          tr.taxRate != null
            ? `${tr.name} [${tr.taxRate.toString()}%]`
            : tr.name,
      })
    );
    return opts;
  };

  const sortSelectionsByReturnType = (returnType: string) => {
    setSelectedReturnType(returnType);
    const hasCA = returnData?.returnTypesEdit?.find(
      (rt) => rt.id === returnType
    )?.cashAccountingEnabled;

    hasCA ? setCashAccounting(true) : setCashAccounting(false);

    const sectionsForReturnType = returnData?.sections?.filter((s) => {
      return (
        s.returnTypeId === returnType ||
        (hasCA && s.name === s.subsections[0].code)
      );
    });
    const selectedOssCountryIds = mappingData?.ossCountryIds || [];
    const shouldFilterByOssCountries =
      isOssReturnType(returnType) && selectedOssCountryIds.length > 0;
    const sec = shouldFilterByOssCountries
      ? sectionsForReturnType?.filter(
          (s) =>
            selectedOssCountryIds.includes(s.id) ||
            s.subsections?.some((ss) => ss.outOfScope)
        )
      : sectionsForReturnType;

    sec?.sort((a, b) => a.sortOrder - b.sortOrder);
    sec && setSections(sec);
    const oocCandidates = sec?.flatMap((s) =>
      s.subsections
        ?.filter((ss) => ss.outOfScope)
        .map((ss) => ss.code)
        .filter(Boolean)
    );

    // If there are multiple `outOfScope: true` subsections, keep using the existing
    // `outOfScopeCode` (usually `OOS`) and only fall back to the first candidate.
    const preferred =
      oocCandidates?.find(
        (c) => c?.toUpperCase() === outOfScopeCode?.toUpperCase()
      ) ??
      oocCandidates?.find((c) => c?.toUpperCase() === 'OOS') ??
      oocCandidates?.[0];

    preferred && setOutOfScopeCode(preferred);
  };

  const clearMappingsForRemovedOssSections = (removedSectionIds: string[]) => {
    if (!returnData?.sections || removedSectionIds.length === 0) return;

    const removedSubsections = returnData.sections
      .filter((s) => removedSectionIds.includes(s.id))
      .flatMap((s) => s.subsections);

    if (removedSubsections.length === 0) return;

    const removedSubsectionCodes = removedSubsections.map((ss) => ss.code);

    const formFieldUpdates: Record<string, undefined> = {};
    removedSubsections.forEach((ss) => {
      const fieldName =
        ss.outOfScope &&
        (!outOfScopeCode || ss.code === outOfScopeCode)
          ? 'outOfScope'
          : ss.code;
      formFieldUpdates[fieldName] = undefined;
    });
    form2.setFieldsValue(formFieldUpdates);

    setSelectedRates((prev) => {
      const updatedRates = prev.map((sr) => ({
        ...sr,
        location: sr.location.filter(
          (loc) => !isOssCodeAmongRemoved(loc, removedSubsectionCodes)
        ),
      }));
      setSelectedRatesCopy([...updatedRates]);

      // Keep full option lists; MapTaxRates derives filtered options from selectedRates.
      const opts = prepareTaxRateOptions();
      setTaxRateOptions(opts);
      setOutOfScopeOptions(opts);

      return updatedRates;
    });

    // Prune Redux jsonMapping too — MapTaxRates rehydrates from it on every
    // mappingData change, so leaving stale country codes would resurrect tags.
    // Read latest from the store so a stale render closure cannot overwrite a
    // mapping that loadExistingMapping just dispatched.
    const latestMappingData = getMappingData(store.getState() as any);
    if (latestMappingData?.jsonMapping?.length) {
      const cleanedJsonMapping = pruneCodesFromJsonMapping(
        latestMappingData.jsonMapping,
        removedSubsectionCodes
      );
      setMappingData({
        ...latestMappingData,
        jsonMapping: cleanedJsonMapping,
      });
    }

    markSectionTouched(true);
  };

  const addUnmappedToOutOfScope = () => {
    const emptyLocations = selectedRates.filter(
      (sr) => sr.location.length === 0
    );
    if (emptyLocations.length === 0) {
      const message: MessageData = {
        title: t('common.attention') + '!',
        description: t('returns.mapping.all-rates-mapped'),
        type: MessageStates.INFO,
      };
      dispatch(turnMessageOn(message));
    } else {
      const md: SelectedRate[] = [];
      const oosValue: string[] = [];
      if (returnData?.taxRates) {
        returnData?.taxRates.map((rt) => {
          const nIndex = selectedRates.findIndex(
            (sr) => sr.value === rt.taxType
          );
          let mdRow: SelectedRate;
          if (nIndex === -1) {
            mdRow = {
              value: rt.taxType,
              location: [outOfScopeCode],
              cashAccounting: false,
              reverseCharge: null,
              taxRateType: TaxRateType.INPUT,
            };
            oosValue.push(rt.taxType);
          } else if (selectedRates[nIndex].location.length === 0) {
            const location = [outOfScopeCode];
            mdRow = { ...selectedRates[nIndex], location };
            oosValue.push(rt.taxType);
          } else {
            mdRow = { ...selectedRates[nIndex] };
            if (mdRow.location[0] === outOfScopeCode) oosValue.push(rt.taxType);
          }

          md.push(mdRow);
        });

        updateSelectedRates(md);
        handleTaxRangeChange(oosValue, true, '');
        form2.setFieldsValue({
          ['outOfScope']: oosValue,
        });
      }
    }
  };

  const handleCashAccounting = (rateId: string) => {
    const tempSelRates: SelectedRate[] = [...selectedRatesCopy];
    const nIndex = tempSelRates.findIndex((tsr) => tsr.value === rateId);
    if (nIndex > -1) {
      tempSelRates[nIndex] = {
        ...tempSelRates[nIndex],
        cashAccounting: !tempSelRates[nIndex]?.cashAccounting,
      };
      setSelectedRatesCopy(tempSelRates);
    }
  };

  const handleReverseCharge = (rateId: string, val: string | null) => {
    const value = val !== null ? parseFloat(val) : null;
    const tempReverseCharges: ReverseCharge[] = [...reverseCharges];
    const nIndex = tempReverseCharges.findIndex((trc) => trc.rateId === rateId);
    if (nIndex > -1) {
      const used =
        value !== null && value > 0
          ? tempReverseCharges[nIndex].used
          : !tempReverseCharges[nIndex].used;
      const amount =
        value !== null && value > 0 ? value : tempReverseCharges[nIndex].amount;

      tempReverseCharges[nIndex] = {
        ...tempReverseCharges[nIndex],
        used,
        amount,
      };
      setReverseCharges(tempReverseCharges);
    }
  };

  const fillCashAccounting = (value: boolean) => {
    const tempSelRates: SelectedRate[] = [];

    selectedRatesCopy.forEach((sr) => {
      tempSelRates.push({ ...sr, cashAccounting: value });
    });
    setSelectedRatesCopy(tempSelRates);
  };

  const resetReverseCharge = () => {
    const tempReverseCharges: ReverseCharge[] = [];

    reverseCharges.forEach((rc) => {
      tempReverseCharges.push({ ...rc, amount: null, used: false });
    });
    setReverseCharges(tempReverseCharges);
  };

  const saveCashAccounting = () => {
    markSectionTouched(true);
    setSelectedRates(selectedRatesCopy);
  };

  const saveReverseCharges = () => {
    if (
      reverseCharges.find(
        (rc) => (rc.amount === 0 || rc.amount === null) && rc.used
      )
    ) {
      const message: MessageData = {
        title: t('common.problem-saving') + '!',
        description: t('submissions.reverse-charge-save-error'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      return false;
    } else {
      markSectionTouched(true);
      const tempSelectedRates = [...selectedRates];
      reverseCharges.forEach((rc) => {
        const nIndex = tempSelectedRates.findIndex(
          (tsr) => tsr.value === rc.rateId
        );
        if (nIndex > -1)
          if (rc.used) {
            tempSelectedRates[nIndex].reverseCharge = rc.amount;
          } else tempSelectedRates[nIndex].reverseCharge = null;
      });

      setSelectedRates(tempSelectedRates);
      return true;
    }
  };

  const loadCashAccounting = () => {
    setSelectedRatesCopy([...selectedRates]);
  };

  const loadReverseCharge = () => {
    setReverseCharges(getEmptyReverseChargeState());
  };

  const getEmptyReverseChargeState = () => {
    const revCharges: ReverseCharge[] = [];
    selectedRates.forEach((sr) => {
      const rateId = sr.value;
      const used = sr.reverseCharge !== null && sr.reverseCharge !== 0;
      const amount = sr.reverseCharge;
      const revCharge: ReverseCharge = { rateId, used, amount };
      revCharges.push(revCharge);
    });

    return revCharges;
  };

  const handleTaxRangeChange = React.useCallback(
    (rateIds: string[], outOfScope: boolean, code: string) => {
      markSectionTouched(true);
      const specialCodes =
        sections
          ?.flatMap((s) => s.subsections)
          .filter((ss) => ss.outOfScope)
          .map((ss) => ss.code) ?? [];
      const isSpecialCode = (val: string) => specialCodes.includes(val);
      const rateIdSet = new Set(rateIds);

      setSelectedRates((prevSelectedRates) => {
        setOldSelectedRates(prevSelectedRates);

        if (outOfScope) {
          const next = prevSelectedRates.map((sr) => {
            if (rateIdSet.has(sr.value)) {
              if (
                sr.location.length === 1 &&
                sr.location[0] === outOfScopeCode
              ) {
                return sr;
              }
              return { ...sr, location: [outOfScopeCode] };
            }
            if (sr.location.includes(outOfScopeCode)) {
              return { ...sr, location: [] };
            }
            return sr;
          });
          rateIds.forEach((r) => {
            if (next.findIndex((sr) => sr.value === r) === -1) {
              next.push({
                value: r,
                location: [outOfScopeCode],
                cashAccounting: false,
                reverseCharge: null,
                taxRateType: TaxRateType.INPUT,
              });
            }
          });
          return next;
        }

        const next = prevSelectedRates.map((sr) => {
          if (rateIdSet.has(sr.value)) {
            if (isSpecialCode(code)) {
              if (sr.location.length === 1 && sr.location[0] === code) {
                return sr;
              }
              return { ...sr, location: [code] };
            }
            if (sr.location.includes(code)) return sr;
            return { ...sr, location: [...sr.location, code] };
          }
          const locIndex = sr.location.indexOf(code);
          if (locIndex > -1) {
            const location = sr.location.filter((l) => l !== code);
            return { ...sr, location };
          }
          return sr;
        });
        rateIds.forEach((ri) => {
          if (next.findIndex((sr) => sr.value === ri) === -1) {
            next.push({
              value: ri,
              location: [code],
              cashAccounting: false,
              reverseCharge: null,
              taxRateType: TaxRateType.INPUT,
            });
          }
        });
        return next;
      });
    },
    [markSectionTouched, sections, outOfScopeCode]
  );

  const loadExistingMapping = (mapId: string, isTemplate = false) => {
    const savingMapId = isTemplate ? mappingId : mapId;
    const selMap = mappings?.find((m) => m.mappingId === mapId);
    const savingPeriodFromDate = isTemplate
      ? mappingData?.periodFromDate
      : selMap?.periodFromDate;
    const savingReturnDueDate = isTemplate
      ? mappingData?.returnDueDate
      : selMap?.returnDueDate;
    const savingReturnFrequency = isTemplate
      ? mappingData?.returnFrequency
      : selMap?.returnFrequency;
    let jsonMapping;
    if (selMap?.jsonMapping) {
      try {
        const parsedMapping = JSON.parse(selMap.jsonMapping);

        jsonMapping = [...parsedMapping?.mapping];
        if (isTemplate) {
          jsonMapping = remapTemplateMappingRates(
            jsonMapping,
            returnData?.taxRates
          );
        }
      } catch (error) {
        console.error('Error parsing JSON:', error);
      }
    }
    if (
      returnData?.taxRates &&
      returnData.taxRates.length < jsonMapping.length
    ) {
      const toDelete = jsonMapping.filter(
        (jm) =>
          returnData.taxRates?.findIndex((tr) => tr.taxType === jm.taxType) ===
          -1
      );
      toDelete.map((td) => {
        jsonMapping.splice(
          jsonMapping.findIndex((jm) => jm.taxType === td.taxType),
          1
        );
      });
    }
    const typeId = returnData?.returnTypesEdit?.find(
      (rt) => rt.id === selMap?.returnTypeId
    )?.id;
    const ossCountryIds = deriveOssCountryIdsFromJsonMapping(
      jsonMapping,
      returnData?.sections,
      typeId
    );
    // Do NOT write prevOssCountryIdsRef here. Updating it before Redux
    // mappingData catches up makes the OSS-country effect treat every country
    // as removed (stale current=[] vs new prev=full) and wipe jsonMapping.

    // Drop any in-memory AI / prior-session mapping so it cannot overwrite this edit.
    dispatch(resetAiMappingStatus());
    resetSelectedRates();
    form2.resetFields();

    const mapData = {
      mappingId: savingMapId || undefined,
      templateMappingId: mapId,
      typeId,
      countryId: returnData?.returnTypesEdit?.find(
        (rt) => rt.id === selMap?.returnTypeId
      )?.countryId,
      ossCountryIds,
      mappedCashAccounting: selMap?.cashAccounting,
      periodFromDate: moment(savingPeriodFromDate).format('YYYY-MM-DD'),
      returnDueDate: moment(savingReturnDueDate).format('YYYY-MM-DD'),
      returnFrequency: savingReturnFrequency,
      jsonMapping,
    };
    setMappingData(mapData);
  };

  const resetForm = () => {
    const opts: TaxRateOption[] = prepareTaxRateOptions();
    const jsonMapping: MappingTaxRate[] = [];
    const mapData: MappingData = { ...mappingData, jsonMapping };

    resetSelectedRates();
    setMappingData(mapData);
    form2.resetFields();
    setTaxRateOptions(opts);
    setOutOfScopeOptions(opts);
  };

  React.useEffect(() => {
    if (!preLoading && returnData?.taxRates) {
      const retTypes = returnData?.returnTypesEdit;
      if (editMode && retTypes && retTypes.length > 0 && mappingId) {
        loadExistingMapping(mappingId);
      }
    }
  }, [preLoading, mappings, returnData]);

  React.useEffect(() => {
    if (mappingData && mappingData.mappingId) {
      setSelectedReturnType(mappingData.typeId);
    }
    let entity: any = '';
    let country: any = '';
    let typeName: any = '';
    let firstPeriod: any = '';
    let firstDueDate = '';
    if (mappingData) {
      if (mappingData.typeId) {
        const curType = returnData?.returnTypesEdit?.find(
          (rt) => rt.id === mappingData?.typeId
        );
        typeName = curType?.name;
        country = curType?.countryName;
        if (mappingData.typeChanged) {
          setSectionTouched(false);
          //setDataSaved(true);
          sortSelectionsByReturnType(mappingData?.typeId);
          resetSelectedRates();
          form2.resetFields();
          // Drop previous return's mapping payload so hydrate cannot re-apply
          // box codes (e.g. OSS country codes) onto the new return type.
          setMappingData({
            ...mappingData,
            typeChanged: false,
            jsonMapping: [],
            templateMappingId: undefined,
            ossCountryIds: [],
          });
        }
      }
      if (mappingData.periodFromDate && mappingData.returnFrequency) {
        firstPeriod = calculateNextPeriod(
          mappingData.periodFromDate,
          mappingData.returnFrequency
        )[0];
        if (mappingData.returnDueDate) firstDueDate = mappingData.returnDueDate;
      }
      if (organisations && conId) {
        const selectedOrg = organisations.find(
          (o) => o.connectionId === conId
        );
        entity = selectedOrg?.friendlyName;
        setStepOneData({
          entity,
          country,
          typeName,
          firstPeriod,
          firstDueDate,
          providerName: selectedOrg?.providerName,
          shortCode: selectedOrg?.shortCode,
          organisationId: selectedOrg?.uuid,
        });
      } else {
        setStepOneData({
          entity,
          country,
          typeName,
          firstPeriod,
          firstDueDate,
        });
      }

      if (
        !defaultTaxRatesData ||
        defaultTaxRatesData.defaultTaxRates.length === 0
      ) {
        dispatch(fetchDefaultTaxRatesRequest(mappingData.typeId || '', conId));
      }
    }
  }, [mappingData]);

  React.useEffect(() => {
    if (!mappingData?.typeId) return;

    const currentOssIds = mappingData?.ossCountryIds || [];
    const prevOssIds = prevOssCountryIdsRef.current;

    if (
      isOssReturnType(mappingData.typeId) &&
      prevOssIds !== undefined &&
      returnData?.sections
    ) {
      const removedSectionIds = prevOssIds.filter(
        (id) => !currentOssIds.includes(id)
      );
      if (removedSectionIds.length > 0) {
        // Ignore load races where this render still has empty ossCountryIds
        // but a newer mappingData (with countries) is already in the store.
        const latestOssIds =
          getMappingData(store.getState() as any)?.ossCountryIds || [];
        const isStaleEmptyCurrent =
          currentOssIds.length === 0 && latestOssIds.length > 0;
        if (!isStaleEmptyCurrent) {
          clearMappingsForRemovedOssSections(removedSectionIds);
        }
      }
    }

    prevOssCountryIdsRef.current = currentOssIds;
    // Re-apply section filtering when OSS selection changes in the reopened
    // "Edit period / dates" drawer, so mapping boxes refresh immediately.
    sortSelectionsByReturnType(mappingData.typeId);
  }, [mappingData?.typeId, mappingData?.ossCountryIds, returnData?.sections]);

  React.useEffect(() => {
    if (!conId || !mappingData?.typeId) return;
    if (loading || preLoading || taxRatesLoading) return;
    if (
      returnData?.connectionId === conId &&
      returnData.sectionsReturnTypeId === mappingData.typeId
    ) {
      return;
    }
    if (
      returnData?.connectionId &&
      returnData.connectionId !== conId
    ) {
      return;
    }
    const requestKey = `${conId}:${mappingData.typeId}`;
    if (requestedSectionsKeyRef.current === requestKey) return;
    requestedSectionsKeyRef.current = requestKey;
    dispatch(
      fetchAddReturnDataRequest(
        conId,
        returnData?.connectionId === conId ? returnData : undefined,
        mappingData.typeId
      )
    );
  }, [
    conId,
    dispatch,
    loading,
    mappingData?.typeId,
    preLoading,
    returnData,
    taxRatesLoading,
  ]);

  React.useEffect(() => {
    if (returnData?.taxRates) {
      if (!editMode) {
        const opts: TaxRateOption[] = prepareTaxRateOptions();
        setTaxRateOptions(opts);
        setOutOfScopeOptions(opts);
      }
      resetSelectedRates();
      mappingData?.typeId && sortSelectionsByReturnType(mappingData?.typeId);
    }
  }, [returnData]);

  React.useEffect(() => {
    currentUser && checkPageLoaded(location.pathname, routes.returns.root);
    localStorage.removeItem('refreshCountMapping');
  }, []);

  const retData = () => {
    const selectedOrg = mappedOrganisations?.find(
      (mo) => mo.connectionId === conId
    );

    const returnTypes = selectedOrg?.returnTypes;
    const returnTypesEdit = selectedOrg?.returnTypesEdit;
    const taxRatesSelector = getTaxRatesForConnection(conId);
    const taxRates = taxRatesSelector(store.getState());
    const selMapping = selectedOrg?.returns.find(
      (r) => r.mappingId === mappingId
    );
    const sections = selMapping?.sections ? selMapping.sections : [];
    return {
      returnTypes,
      returnTypesEdit,
      sections,
      connectionId: conId || undefined,
      taxRates,
    };
  };

  React.useEffect(() => {
    if (mappedOrganisations) {
      const cnt = localStorage.getItem('refreshCountMapping');
      if (!cnt) localStorage.setItem('refreshCountMapping', '1');
      else localStorage.setItem('refreshCountMapping', '2');

      const cnt2 = localStorage.getItem('refreshCountMapping');
      if (cnt2 === '1' && conId) {
        const selectedOrg = mappedOrganisations.find(
          (mo) => mo.connectionId === conId
        );
        const selMapping = selectedOrg?.returns.find(
          (r) => r.mappingId === mappingId
        );
        const currentMapping = getMappingData(store.getState() as any);
        const currentReturnData = getReturnData(store.getState() as any);
        const returnTypeId =
          selMapping?.returnTypeId || currentMapping?.typeId;
        const loadedReturnData =
          currentReturnData?.connectionId === conId
            ? currentReturnData
            : retData();
        dispatch(
          fetchAddReturnDataRequest(conId, loadedReturnData, returnTypeId)
        );
      } else {
        localStorage.removeItem('refreshCountMapping');
      }
    }
  }, [mappedOrganisations]);

  const resetSelectedRates = () => {
    if (returnData?.taxRates) {
      const tempSelRates: SelectedRate[] = [];
      returnData.taxRates.forEach((tr) => {
        const value = tr.taxType;
        const location = [];
        const cashAccounting = false;
        const reverseCharge = null;
        const taxRateType = TaxRateType.INPUT;
        tempSelRates.push({
          value,
          location,
          cashAccounting,
          reverseCharge,
          taxRateType,
        });
      });

      setSelectedRates(tempSelRates);
    }
  };
  const loadTemplateMappings = () => {
    if (mappings) {
      const otherMaps = mappings.filter((m) => {
        if (
          m.mappingId === mappingId ||
          m.returnTypeId !== selectedReturnType
        ) {
          return false;
        }

        const sourceOrganisation = organisations?.find(
          (o) => o.uuid === m.organisationId
        );

        // Exclude only provider-group entities themselves.
        return (
          !!sourceOrganisation &&
          sourceOrganisation.providerName?.toLowerCase() !==
            Provider.GROUP.toLowerCase()
        );
      });

      const tmplMappings: TemplateMapping[] = [];
      otherMaps.map((om) => {
        const orName = organisations?.find(
          (o) => o.uuid === om.organisationId
        )?.friendlyName;
        const createdDate = formatDisplayDate(
          om.createdDate,
          'DD/MM/YYYY',
          i18n.language
        );
        const organisationName = orName ? orName : '';
        const mappingId = om.mappingId;
        const countryIso = organisations?.find(
          (o) => o.uuid === om.organisationId
        )?.country;
        const country = localizeCountry(countryIso, countryIso);
        tmplMappings.push({
          createdDate,
          organisationName,
          mappingId,
          country,
        });
      });

      setTemplateMappings(tmplMappings);
    }
  };

  const onSubmit = (stayOnPage = true) => {
    setSectionTouched(false);
    let passed = false;
    const emptyLocations = selectedRates.filter(
      (sr) => sr.location.length === 0
    );
    if (
      !returnData?.taxRates ||
      //selectedRates.length !== returnData.taxRates.length ||
      emptyLocations.length > 0
    ) {
      const message: MessageData = {
        title: t('common.problem-saving') + '!',
        description: t('returns.mapping.rates-must-be-mapped'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
    } else {
      const jsonMapping: MappingTaxRate[] = [];
      let mappedCashAccounting = false;
      let mappedReverseCharges = false;

      selectedRates.map((sr) => {
        const rate: TaxRate | undefined = returnData?.taxRates?.find(
          (tr) => tr.taxType === sr.value
        );
        if (rate) {
          let codes: string | undefined = '';

          if (sr.cashAccounting) {
            mappedCashAccounting = true;
            codes =
              ',' +
              returnData?.sections?.find(
                (s) => s.name === s.subsections[0].code
              )?.subsections[0].code;
          }
          sr.location.map((l) => {
            codes = `${codes},${l}`;
          });
          const reverseCharge = sr.reverseCharge;
          const taxRateType = sr.taxRateType;
          if (reverseCharge !== null && reverseCharge > 0)
            mappedReverseCharges = true;
          const taxCodeName = rate?.name;
          const taxRate = rate?.taxRate?.toFixed(2);
          const taxType = rate?.taxType;

          codes = codes?.substring(1);
          jsonMapping.push({
            taxCodeName,
            taxRate,
            taxType,
            codes,
            reverseCharge:
              reverseCharge !== null && reverseCharge > 0
                ? reverseCharge.toFixed(2)
                : null,
            taxRateType,
          });
        }
      });

      const md: MappingData = {
        ...mappingData,
        mappedCashAccounting,
        mappedReverseCharges,
        jsonMapping,
      };
      const findOutputs = jsonMapping.findIndex(
        (jm) =>
          jm.codes !== outOfScopeCode && jm.taxRateType === TaxRateType.OUTPUT
      );
      const saveCallback = () => {
        passed = true;
        setDataSaved(true);
        saveMappingData(md, stayOnPage);
      };

      if (findOutputs === -1) {
        const modalData: ModalData = {
          type: WindowType.WARNING,
          title: t('submissions.mapping-might-be-incorrect-title'),
          icon: <img src={GenTaxImage} alt="" />,
          component: (
            <ConfirmBoxContainer
              callback={saveCallback}
              showCancelBtn
              okText={t('common.continue')}
              description={t('submissions.mapping-no-output-rates-description')}
            />
          ),
        };

        dispatch(turnModalOn(modalData));
      } else {
        saveCallback();
      }
    }

    return passed;
  };

  const dismissNavigationWarning = React.useCallback(() => {
    unsavedNavigationModalShownRef.current = false;
    pendingNavigationWarningsRef.current = [];
    isLeavingWithoutSaveRef.current = false;
    blocker.reset?.();
  }, [blocker]);

  const completeBlockedNavigation = React.useCallback(() => {
    isLeavingWithoutSaveRef.current = true;
    unsavedNavigationModalShownRef.current = false;
    pendingNavigationWarningsRef.current = [];

    const proceed = blockedProceedRef.current ?? blocker.proceed;
    const nextLocation = blocker.location;
    blockedProceedRef.current = undefined;

    if (proceed && blocker.state === 'blocked') {
      proceed();
      return;
    }

    if (nextLocation) {
      blocker.reset?.();
      navigate({
        pathname: nextLocation.pathname,
        search: nextLocation.search || '',
      });
    }
  }, [blocker, navigate]);

  const showNextNavigationWarning = React.useCallback(() => {
    const nextWarning = pendingNavigationWarningsRef.current.shift();

    if (!nextWarning) {
      skipUnsavedNavigationBlockRef.current = true;
      skipMappingNavigationWarningsRef.current = true;
      completeBlockedNavigation();
      return;
    }

    if (nextWarning === 'unsaved') {
      const continueWithoutSaving = () => {
        sectionTouchedRef.current = false;
        setSectionTouched(false);
        dispatch(resetAiMappingStatus());
        dispatch(clearMappingData());
        showNextNavigationWarning();
      };

      const saveMappingFromBlocker = () => {
        unsavedNavigationModalShownRef.current = false;
        pendingNavigationWarningsRef.current = [];
        blocker.reset?.();
        onSubmit(false);
      };

      dispatch(
        turnModalOn({
          type: WindowType.WARNING,
          title: t('submissions.mapping-not-saved-title'),
          icon: <img src={GenTaxImage} alt="" />,
          onClose: dismissNavigationWarning,
          component: (
            <ConfirmBoxContainer
              callback={continueWithoutSaving}
              callback2={saveMappingFromBlocker}
              showCancelBtn={false}
              param2={oldSelectedRates}
              okText={t('vat-report.continue-without-saving')}
              okText2={t('submissions.save-mapping')}
              description={t('submissions.mapping-go-back-description')}
            />
          ),
        })
      );
      return;
    }

    const unmappedSections = getUnmappedOssCountrySections(
      mappingDataRef.current?.ossCountryIds || [],
      returnData?.sections,
      mappingDataRef.current?.typeId,
      getMappedSubsectionCodesFromSelectedRates(selectedRatesRef.current)
    );
    const countryNames = unmappedSections
      .map((section) => localizeCountry(section.name, section.name))
      .join(', ');

    const continueLeaving = () => {
      showNextNavigationWarning();
    };

    dispatch(
      turnModalOn({
        type: WindowType.WARNING,
        title: t('submissions.oss-unmapped-countries-title'),
        icon: <img src={GenTaxImage} alt="" />,
        onClose: dismissNavigationWarning,
        component: (
          <ConfirmBoxContainer
            callback={continueLeaving}
            cancelCallback={dismissNavigationWarning}
            showCancelBtn={true}
            okText={t('common.continue')}
            description={t('submissions.oss-unmapped-countries-description', {
              countries: countryNames,
            })}
          />
        ),
      })
    );
  }, [
    blocker,
    completeBlockedNavigation,
    dispatch,
    dismissNavigationWarning,
    oldSelectedRates,
    onSubmit,
    returnData?.sections,
    t,
    localizeCountry,
  ]);

  React.useEffect(() => {
    if (blocker.state !== 'blocked' || isLeavingWithoutSaveRef.current) {
      return;
    }

    const nextLocation = blocker.location;
    if (!nextLocation) {
      return;
    }

    if (unsavedNavigationModalShownRef.current) {
      return;
    }

    const warnings = getPendingNavigationWarnings(nextLocation.pathname);

    if (warnings.length === 0) {
      skipUnsavedNavigationBlockRef.current = true;
      proceedBlockedNavigation();
      return;
    }

    unsavedNavigationModalShownRef.current = true;
    pendingNavigationWarningsRef.current = warnings;
    showNextNavigationWarning();
  }, [
    blocker.state,
    blocker.location,
    getPendingNavigationWarnings,
    proceedBlockedNavigation,
    showNextNavigationWarning,
  ]);

  const updateTaxRateType = (type: TaxRateType, rateId: string | any[]) => {
    const tempSelRates = [...selectedRates];

    const tempSelRates2: SelectedRate[] = [];
    tempSelRates.forEach((a) => {
      if (typeof rateId === 'string') {
        if (a.value === rateId) {
          tempSelRates2.push({ ...a, taxRateType: type });
        } else tempSelRates2.push(a);
      } else {
        if (rateId.findIndex((r) => r === a.value) !== -1) {
          tempSelRates2.push({ ...a, taxRateType: type });
        } else tempSelRates2.push({ ...a, taxRateType: TaxRateType.INPUT });
      }
    });

    setSelectedRates(tempSelRates2);
  };

  const mapWithAI = () => {
    const selectedOrg = organisations?.find((o) => o.connectionId === conId);
    if (selectedOrg) {
      applyAiMappingRef.current = true;
      dispatch(mapTaxRatesToBoxes(selectedOrg.uuid, mappingData?.typeId));
    }
  };

  React.useEffect(() => {
    // Only apply AI results produced by an explicit click on this screen.
    // Stale Redux AI payloads from a cancelled OSS draft must not overwrite
    // another return (e.g. Ireland VAT3) when opening edit.
    if (
      !applyAiMappingRef.current ||
      !taxRatesToBoxesMapping ||
      !returnData?.taxRates ||
      !returnData?.sections ||
      !mappingData?.typeId
    ) {
      return;
    }

    // Only map boxes that belong to the return type AI was run for — never all sections.
    const sectionsForReturnType = returnData.sections.filter(
      (section) =>
        section.returnTypeId === mappingData.typeId ||
        section.name === section.subsections?.[0]?.code
    );

    const subsectionMap = new Map<string, string>();
    sectionsForReturnType.forEach((section) => {
      section.subsections?.forEach((sub) => {
        subsectionMap.set(sub.id, sub.code);
      });
    });

    const jsonMapping: MappingTaxRate[] = taxRatesToBoxesMapping.map(
      (mapping: any) => {
        const taxRate = returnData.taxRates?.find(
          (tr) => tr.taxType === mapping.taxId
        );
        const codes =
          mapping.boxes
            ?.map((box: any) => subsectionMap.get(box.id))
            .filter(Boolean)
            .join(',') || '';

        return {
          taxCodeName: taxRate?.name,
          taxRate: taxRate?.taxRate?.toFixed(2),
          taxType: mapping.taxId,
          codes,
          reverseCharge: null,
          taxRateType:
            mapping.taxDirection === 'OUTPUT'
              ? TaxRateType.OUTPUT
              : TaxRateType.INPUT,
        };
      }
    );

    applyAiMappingRef.current = false;
    markSectionTouched(true);
    setMappingData({
      ...mappingData,
      templateMappingId: AI_TEMPLATE_MAPPING_ID,
      jsonMapping,
    });
  }, [markSectionTouched, returnData?.sections, returnData?.taxRates, taxRatesToBoxesMapping]);

  // Leaving the mapping page must not leave AI results in Redux for the next edit.
  React.useEffect(() => {
    return () => {
      dispatch(resetAiMappingStatus());
    };
  }, [dispatch]);

  const onAiMappingDismiss = React.useCallback(() => {
    applyAiMappingRef.current = false;
    dispatch(resetAiMappingStatus());
  }, [dispatch]);

  return (
    <React.Fragment>
      <Helmet>
        <title>{`${t('submissions.add-tax-return-title')} | ${process.env.PROJECT_NAME}`}</title>
        <meta name="description" content={t('returns.page-meta-description')} />
      </Helmet>
      <Spin
        spinning={taxRatesLoading || loading || !sections}
        style={{ minHeight: '100vh' }}
        size="large"
        //fullscreen
      >
        {!sections ||
          (sections.length !== 0 && (
            <MappingForm
              preLoading={preLoading}
              taxRatesLoading={taxRatesLoading}
              loading={loading}
              returnData={returnData}
              mappingData={mappingData}
              stepOneData={stepOneData}
              form2={form2}
              editMode={editMode}
              selectedRates={selectedRates}
              selectedRatesCopy={selectedRatesCopy}
              reverseCharges={reverseCharges}
              outOfScopeCode={outOfScopeCode}
              cashAccounting={cashAccounting}
              handleCashAccounting={handleCashAccounting}
              handleReverseCharge={handleReverseCharge}
              addUnmappedToOutOfScope={addUnmappedToOutOfScope}
              updateSelectedRates={updateSelectedRates}
              sections={sections}
              taxRateOptions={taxRateOptions}
              outOfScopeOptions={outOfScopeOptions}
              handleTaxRangeChange={handleTaxRangeChange}
              setOptions={setOptions}
              prepareTaxRateOptions={prepareTaxRateOptions}
              hasTemplates={templateMappings.length > 0}
              loadTemplateMappings={loadTemplateMappings}
              openTemplateWindow={openTemplateWindow}
              openDefaultMappingsWindow={openDefaultMappingsWindow}
              resetForm={resetForm}
              editInitialData={editInitialData}
              saveCashAccounting={saveCashAccounting}
              saveReverseCharges={saveReverseCharges}
              resetReverseCharge={resetReverseCharge}
              fillCashAccounting={fillCashAccounting}
              loadCashAccounting={loadCashAccounting}
              loadReverseCharge={loadReverseCharge}
              onSubmit={onSubmit}
              updateTaxRateType={updateTaxRateType}
              handlePreferences={handlePreferences}
              defaultTaxRatesData={defaultTaxRatesData}
              providerName={getProviderName(
                organisations?.find((o) => o.connectionId === conId)
                  ?.providerName
              )}
              mapWithAI={mapWithAI}
              aiMappingStatus={aiMappingStatus}
              onAiMappingDismiss={onAiMappingDismiss}
            />
          ))}
      </Spin>
    </React.Fragment>
  );
};
